import { Checkbox, Label, Space, WithEllipsisTooltip } from '@procurify/ui'

import { ItemPrice } from '@webapp/components/ItemPrice'
import { TableTaxField } from '@webapp/components/TableTaxField'
import { TaxInclusiveCheckbox } from '@webapp/components/TaxInclusiveCheckbox'
import { UserAvatar } from '@webapp/components/UserAvatar'
import { WithCurrencyConversionTooltip } from '@webapp/components/WithCurrencyConversionTooltip'
import { BridgeLink } from '@webapp/core/components/BridgeLink'
import { Price } from '@webapp/core/components/Price'
import { type Currency } from '@webapp/models'
import { Quantity } from '../../Quantity'
import { TableCustomField } from '../../TableCustomField'
import { APItemAccountCodeCell } from '../components/APItemAccountCodeCellDeprecated'
import { APItemDescriptionCell } from '../components/APItemDescriptionCellDeprecated'
import { APItemMatchedPOItemCell } from '../components/APItemMatchedPOItemCellDeprecated'
import { APItemReceivedCell } from '../components/APItemReceivedCellDeprecated'
import { APItemTaxCell } from '../components/APItemTaxCellDeprecated'
import { APItemUnitCostCell } from '../components/APItemUnitCostCellDeprecated'
import { BudgetAccountCodeCell } from '../components/BudgetAccountCodeCell'
import { BudgetDepartmentCell } from '../components/BudgetDepartmentCell'
import { DateCell } from '../components/DateCell'
import { ItemDescriptionQuantityCell } from '../components/ItemDescriptionQuantityCell/ItemDescriptionQuantityCell'
import { ItemReceivedQuantityCell } from '../components/ItemReceivedQuantityCell'
import { OrderItemDescriptionCell } from '../components/OrderItemDescriptionCell'
import { OrderItemStatusCell } from '../components/OrderItemStatusCell'
import { UnbilledItemRefCell } from '../components/UnbilledItemRefCell'
import { VendorCell } from '../components/VendorCell/VendorCell'
import { type ColumnTypeDefaults } from '../types/DataTable.types'

export const COLUMN_TYPE_DEFAULTS: ColumnTypeDefaults = {
  BASIC: { BodyCellComponent: ({ data }) => <>{data}</> },
  QUANTITY: {
    loaderWidth: 60,
    textAlign: 'right',
    BodyCellComponent: ({ data }) =>
      data ? <Quantity quantity={data} /> : <>--</>,
  },
  UNIT_TYPE: {
    loaderWidth: 60,
    BodyCellComponent: ({ data }) => <>{data}</>,
  },
  PRICE: {
    loaderWidth: 100,
    textAlign: 'right',
    BodyCellComponent: ({
      data: {
        bold = true,
        currency,
        currencyName,
        price,
        recurringFrequency,
        recurringPrice,
        renderZeroPrice = false,
        showCurrencyConversionTooltip = true,
      },
    }) => {
      // TODO: Remove this once DataTable is migrated into core folder
      const legacyCurrency: Currency = currency
        ? {
            id: currency.id,
            name: currency.name,
            rate: currency.rate ? currency.rate.toString() : null,
          }
        : null

      if (recurringPrice) {
        return (
          <WithCurrencyConversionTooltip
            price={recurringPrice}
            currency={legacyCurrency}
            isDisabled={
              currency.base || !currency.rate || !showCurrencyConversionTooltip
            }
          >
            <ItemPrice
              price={recurringPrice}
              currency={currency.name ?? currencyName}
              frequency={recurringFrequency}
            />
          </WithCurrencyConversionTooltip>
        )
      } else if (price || (price === 0 && renderZeroPrice)) {
        return (
          <WithCurrencyConversionTooltip
            price={price}
            currency={legacyCurrency}
            isDisabled={
              currency?.base ||
              !currency?.rate ||
              !showCurrencyConversionTooltip
            }
          >
            <Price
              price={price}
              currency={currency?.name ?? currencyName}
              fontWeight={bold ? 'bold' : 'normal'}
            />
          </WithCurrencyConversionTooltip>
        )
      } else {
        return <>--</>
      }
    },
  },
  LABEL: {
    loaderWidth: 80,
    BodyCellComponent: ({ data }) => {
      const { link, label, text } = data

      if (link) {
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <BridgeLink to={link}>
              <Label label={label} aria-label={data['aria-label']}>
                {text}
              </Label>
            </BridgeLink>
          </div>
        )
      }

      return (
        <Label label={label} aria-label={data['aria-label']}>
          {text}
        </Label>
      )
    },
  },
  CUSTOM_FIELD: {
    loaderWidth: 60,
    BodyCellComponent: ({ data }) =>
      data ? <TableCustomField customfield={data} /> : <>--</>,
  },
  DATE: {
    width: 100,
    loaderWidth: 60,
    BodyCellComponent: ({ data }) => {
      return data.date ? <DateCell {...data} /> : <>--</>
    },
  },
  ORDERITEM_DESCRIPTION: {
    loaderWidth: 125,
    BodyCellComponent: ({ data }) => <OrderItemDescriptionCell {...data} />,
  },
  ORDERITEM_STATUS: {
    loaderWidth: 60,
    BodyCellComponent: ({ data }) => <OrderItemStatusCell {...data} />,
  },
  CHECKBOX: {
    loaderWidth: 10,
    clickable: true,
    BodyCellComponent: ({ data }) => <Checkbox {...data} />,
  },
  SELECT: {
    loaderWidth: 100,
    BodyCellComponent: ({ data }) =>
      data?.renderSelect ? <>{data.renderSelect()}</> : <>--</>,
  },
  UNBILLEDITEM_REF: {
    loaderWidth: 60,
    BodyCellComponent: ({ data }) => <UnbilledItemRefCell item={data} />,
  },
  BUDGET_DEPARTMENT: {
    loaderWidth: 60,
    BodyCellComponent: ({ data }) => <BudgetDepartmentCell {...data} />,
  },
  BUDGET_ACCOUNTCODE: {
    loaderWidth: 60,
    BodyCellComponent: ({ data }) => {
      if (!data || !data?.budget) return <>--</>

      return <BudgetAccountCodeCell {...data} />
    },
  },
  ITEM_DESCRIPTION_QUANTITY: {
    loaderWidth: 125,
    BodyCellComponent: ({ data }) => <ItemDescriptionQuantityCell {...data} />,
  },
  ITEM_RECEIVED_QUANTITY: {
    loaderWidth: 125,
    BodyCellComponent: ({ data }) => <ItemReceivedQuantityCell {...data} />,
  },
  AP_ITEM_DESCRIPTION: {
    loaderWidth: 125,
    BodyCellComponent: ({ data }) => <APItemDescriptionCell {...data} />,
  },
  AP_ITEM_ACCOUNT_CODE: {
    loaderWidth: 125,
    BodyCellComponent: ({ data }) => <APItemAccountCodeCell {...data} />,
  },
  AP_ITEM_UNIT_COST: {
    loaderWidth: 125,
    BodyCellComponent: ({ data }) => <APItemUnitCostCell {...data} />,
  },
  AP_ITEM_MATCHED_PO_ITEM: {
    loaderWidth: 125,
    BodyCellComponent: ({ data }) => <APItemMatchedPOItemCell {...data} />,
  },
  AP_ITEM_RECEIVED: {
    loaderWidth: 125,
    BodyCellComponent: ({ data }) => <APItemReceivedCell {...data} />,
  },
  AP_ITEM_TAX: {
    loaderWidth: 125,
    BodyCellComponent: ({ data }) => <APItemTaxCell {...data} />,
  },
  TAX: {
    width: 1,
    loaderWidth: 36,
    clickable: true,
    BodyCellComponent: ({ data }) => {
      const { isInclusive } = data

      return <TaxInclusiveCheckbox disabled checked={isInclusive} />
    },
  },
  TAX_NAME: {
    width: 1,
    loaderWidth: 60,
    BodyCellComponent: ({ data }) => <>{data.name || '--'}</>,
  },
  TAX_AMOUNT: {
    loaderWidth: 100,
    textAlign: 'right',
    BodyCellComponent: ({ data }) => {
      if (!data) return <>--</>

      return (
        <TableTaxField
          taxAmount={data.taxAmount}
          taxes={data.taxes}
          currency={data.currency}
        />
      )
    },
  },
  VENDOR: {
    loaderWidth: 60,
    clickable: true,
    BodyCellComponent: ({ data }) => (
      <VendorCell
        vendor={data?.vendor}
        otherVendorName={data?.otherVendorName}
        plainText={data.plainText}
        isPending={data.isPending}
      />
    ),
  },
  USER: {
    loaderWidth: 60,
    BodyCellComponent: ({ data }) => {
      if (!data) return <>--</>

      const { fullName, profileImage } = data

      return (
        <Space direction='horizontal' size='small' align='center'>
          {
            <UserAvatar
              url={profileImage}
              size='small'
              circular
              shadow={false}
            />
          }
          <WithEllipsisTooltip title={fullName}>
            <span>{fullName}</span>
          </WithEllipsisTooltip>
        </Space>
      )
    },
  },
}
